import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import { Box, Collapse, List, ListItemButton, styled, SvgIcon, Typography, } from '@mui/material';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ChevronRightIcon from '@heroicons/react/24/solid/ChevronRightIcon';
import { useSidebar } from './SidebarContext';
const pathInPathname = (path, pathname, exact = false) => exact ? pathname === path : pathname.includes(path);
export const SidebarItem = ({ disabled, icon, path, title, exact, external, }) => {
    const { pathname } = useLocation();
    const { closeMobileSidebar } = useSidebar();
    // External entries open in a new tab and never match the current route, so
    // they are never highlighted as active.
    const active = !external && pathInPathname(path, pathname, exact);
    return (_jsxs(ListItemButton, { sx: { px: 2 }, component: external ? 'a' : Link, ...(external
            ? { href: path, target: '_blank', rel: 'noopener noreferrer' }
            : { to: path }), onClick: closeMobileSidebar, children: [_jsx(SidebarIcon, { active: active, fontSize: 'small', children: icon }), _jsx(SidebarLabel, { active: active, disabled: disabled ?? false, children: title })] }));
};
export const SidebarGroup = ({ icon, title, entries, }) => {
    const [open, setIsOpen] = useState(false);
    const { pathname } = useLocation();
    const { closeMobileSidebar } = useSidebar();
    const toggleGroup = () => setIsOpen((prev) => !prev);
    const hasActiveChild = entries.some(({ path, exact }) => pathInPathname(path, pathname, exact));
    const openCollapse = open || hasActiveChild;
    return (_jsxs(_Fragment, { children: [_jsxs(ListItemButton, { sx: { px: 2 }, onClick: toggleGroup, children: [_jsx(SidebarIcon, { active: hasActiveChild, fontSize: 'small', children: icon }), _jsx(SidebarLabel, { active: hasActiveChild, disabled: false, children: title }), _jsx(SidebarIcon, { sx: {
                            fontSize: '1.1rem',
                            transform: `rotateZ(${openCollapse ? '90deg' : '0deg'})`,
                        }, fontSize: 'small', active: false, children: _jsx(ChevronRightIcon, {}) })] }), _jsx(Collapse, { in: openCollapse, timeout: 'auto', unmountOnExit: true, children: _jsx(List, { sx: { ml: 6 }, component: 'div', disablePadding: true, children: entries.map(({ title, path, exact }, index) => {
                        const active = pathInPathname(path, pathname, exact);
                        return (_jsxs(ListItemButton, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                            }, component: Link, to: path, onClick: closeMobileSidebar, children: [active && (_jsx(Box, { sx: {
                                        mr: 3,
                                        backgroundColor: 'common.white',
                                        borderRadius: '50%',
                                        width: '6px',
                                        height: '6px',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '8px',
                                        transform: 'translateY(-50%)',
                                    } })), _jsx(SidebarLabel, { sx: { ml: 4 }, active: active, disabled: false, children: title })] }, index));
                    }) }) })] }));
};
function sidebarMutedColor(theme) {
    const palette = theme.palette;
    return palette?.neutral?.[400] ?? '#9e9e9e';
}
const SidebarIcon = styled(SvgIcon, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
    alignItems: 'center',
    color: sidebarMutedColor(theme),
    display: 'inline-flex',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    ...(active && { color: '#fff' }),
}));
const SidebarLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'disabled',
})(({ theme, active, disabled }) => ({
    color: sidebarMutedColor(theme),
    flexGrow: 1,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '24px',
    whiteSpace: 'nowrap',
    ...(active && { color: theme.palette.common.white }),
    ...(disabled && { color: sidebarMutedColor(theme) }),
}));
